'use client';

import React, { useState } from 'react';
import { Calendar, MapPin, Car, ExternalLink, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { WorkRequest } from './WorkRequestsClient';

interface WorkRequestsListProps {
  requests: WorkRequest[];
}

const statusConfig = {
  synced: { label: 'Synced', icon: CheckCircle2, className: 'text-positive bg-positive/10 border-positive/20' },
  pending: { label: 'Pending', icon: Clock, className: 'text-warning bg-warning/10 border-warning/20' },
  failed: { label: 'Failed', icon: AlertCircle, className: 'text-destructive bg-destructive/10 border-destructive/20' },
};

function WorkRequestCard({ request }: { request: WorkRequest }) {
  const [expanded, setExpanded] = useState(false);
  const status = statusConfig[request.status];
  const StatusIcon = status.icon;

  const formattedDate = (() => {
    const [y, m, d] = request.scheduledDate.split('-');
    return `${d}/${m}/${y}`;
  })();

  return (
    <div className="card-surface rounded-xl border border-border overflow-hidden transition-all duration-200 hover:border-primary/30">
      {/* Card header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Car size={16} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-mono text-sm font-bold text-foreground tracking-wide">{request.vehicleReg}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <MapPin size={11} className="text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground truncate">{request.branch}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${status.className}`}>
              <StatusIcon size={11} />
              {status.label}
            </span>
          </div>
        </div>

        {/* Date + time */}
        <div className="flex items-center gap-2 mt-3">
          <Calendar size={13} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {formattedDate} at {request.scheduledTime}
          </span>
          {request.calendarEventLink && request.calendarEventLink !== '#' && (
            <a
              href={request.calendarEventLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink size={11} />
              View event
            </a>
          )}
        </div>

        {/* Task preview */}
        <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{request.taskDetails}</p>

        {/* Cosmetic items badge + expand */}
        {request.cosmeticItems.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 transition-colors"
          >
            <span className="w-4 h-4 rounded-full bg-accent/10 flex items-center justify-center text-[10px] font-bold text-accent">
              {request.cosmeticItems.length}
            </span>
            cosmetic item{request.cosmeticItems.length !== 1 ? 's' : ''} appended as notes
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {/* Expanded cosmetic items */}
      {expanded && request.cosmeticItems.length > 0 && (
        <div className="border-t border-border px-4 py-3 bg-secondary/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
            Cosmetic Checklist Notes
          </p>
          <ul className="space-y-1.5">
            {request.cosmeticItems.map((item, i) => (
              <li key={`cosmetic-${i}`} className="flex items-start gap-2 text-xs text-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function WorkRequestsList({ requests }: WorkRequestsListProps) {
  return (
    <div className="card-surface rounded-xl border border-border">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Scheduled Requests</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{requests.length} total — sorted by newest first</p>
        </div>
      </div>

      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto scrollbar-thin">
        {requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar size={32} className="text-muted-foreground mb-3 opacity-40" />
            <p className="text-sm font-medium text-muted-foreground">No work requests yet</p>
            <p className="text-xs text-muted-foreground mt-1">Submit the form to schedule your first request</p>
          </div>
        ) : (
          requests.map((request) => (
            <WorkRequestCard key={`wr-${request.id}`} request={request} />
          ))
        )}
      </div>
    </div>
  );
}
